import React, { useEffect } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MainLayout from "../components/layout/MainLayout";
import Breadcrumbs from "../components/common/Breadcrumbs";
import Swal from "sweetalert2";
import PROSAssessmentList from "../pages/Phase-05/PROSAssessment/PROSAssessmentList";
import {
    fetchProsAssessments,
    deleteProsAssessment,
    clearError,
} from "../store/slices/prosAssessmentsSlice";

const PROSAssessments = () => {
    const navigate = useNavigate();
    const { patientId } = useParams(); // Optional: /patient/:id
    const dispatch = useDispatch();

    const { prosAssessments, isLoading, error } = useSelector(
        (state) => state.prosAssessments
    );

    // === Permissions (Replace with real auth later) ===
    const canManage = true;
    const canCreate = true;
    const canEdit = true;
    const canDelete = true;

    // === Fetch on Mount ===
    useEffect(() => {
        dispatch(clearError());
        dispatch(fetchProsAssessments(patientId || null));
    }, [dispatch, patientId]);

    // === Handlers ===
    const handleAddAssessment = () => navigate("/phase05/pros-assessments/add");

    const handleEditAssessment = (id) =>
        navigate(`/phase05/pros-assessments/edit/${id}`);

    const handleDeleteAssessment = async (id) => {
        const result = await Swal.fire({
            title: "Are you sure?",
            text: "This PROS Assessment will be permanently deleted!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteProsAssessment(id)).unwrap();
                Swal.fire({
                    title: "Deleted!",
                    text: "PROS Assessment has been removed.",
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false,
                });
            } catch (err) {
                const errorMsg = err?.errors
                    ? Object.values(err.errors).flat().join("\n")
                    : err || "Failed to delete assessment.";

                Swal.fire({
                    title: "Error",
                    text: errorMsg,
                    icon: "error",
                });
            }
        }
    };

    // === Permission Check ===
    useEffect(() => {
        if (error) {
            Swal.fire({
                title: "Error",
                text: error,
                icon: "error",
                confirmButtonText: "OK",
            });
        }
    }, [error]);

    // === Then Conditional Render ===
    if (!canManage) {
        return (
            <Navigate
                to="/dashboard"
                state={{ error: "You do not have permission to manage PROS Assessments." }}
                replace
            />
        );
    }

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />

                <div className="card">
                    <div className="card-body">
                        {isLoading ? (
                            <div className="d-flex justify-content-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">Loading PROS Assessments...</p>
                            </div>
                        ) : (
                            <PROSAssessmentList
                                assessments={prosAssessments}
                                onAddAssessment={canCreate ? handleAddAssessment : null}
                                onEditAssessment={canEdit ? handleEditAssessment : null}
                                onDeleteAssessment={canDelete ? handleDeleteAssessment : null}
                            />
                        )}
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default PROSAssessments;